<script lang="ts" setup>
import { mdiSofaSingleOutline } from '@mdi/js'

import CouponInput from '@/components/business/coupons/CouponInput.vue'
import DancingDotsLoader from '@/components/DancingDotsLoader.vue'
import NumberIncrement from '@/components/NumberIncrement.vue'
import SkrCheckbox from '@/components/SkrCheckbox.vue'

const props = withDefaults(
  defineProps<{
    pricePlan: PricePlan
    currency: Currency
    billingCycle?: BillingCycle
    minSeats?: number
    isLoading?: boolean
    isDisabled?: boolean
    isFetchingPrice?: boolean
    hasPaymentMethod?: boolean
    preview: PaymentPreview | null
  }>(),
  {
    billingCycle: 'yearly',
    minSeats: 1,
  }
)

const emit = defineEmits<{
  'change-cycle': [cycle: BillingCycle]
  upgrade: [seatsNew?: number, coupon?: string]
  update: [seatsNew?: number, cycle?: BillingCycle, coupon?: string]
}>()

const businessStore = useBusinessStore()

const { t } = useI18n()

const icons = reactive({
  sofa: mdiSofaSingleOutline,
})

const { isChargebeeBusiness, isLocked, isStarterPlan, isTrialing, hasPreviouslyUsedTrial } = storeToRefs(businessStore)

const discountPromoCode = computed(() => businessStore.discountPromoCode || '')

const isStarterUpgrading = computed(() => isStarterPlan.value && hasPreviouslyUsedTrial.value)

const showPromoCodeInput = computed(
  () => isTrialing.value || (discountPromoCode.value && !businessStore.isSMGDiscountApplied) || isStarterUpgrading.value
)

const isPaidChargebeePlan = computed(() => isChargebeeBusiness.value && !isTrialing.value && !isStarterPlan.value)

const upgradeButtonContent = computed(() => {
  if (isMigrated.value) return t('business.upgrade.plan.migrated.upgrade')
  if (props.hasPaymentMethod || isPaidChargebeePlan.value) return t('business.upgrade.plan.pay')
  else return t('global.continue_with_payment_method')
})

// If a business already has a yearly subscription,
// we don't want to allow a change to monthly
const isYearlySubscription = computed(() => {
  return businessStore.currentPricePlan?.cycle === 'YEARLY' && !isLocked.value
})

const selectedBillingCycle = computed<BillingCycle>({
  get() {
    return props.billingCycle
  },
  set(selectedCycle) {
    emit('change-cycle', selectedCycle)
    coupon.value = ''
  },
})

const selectedSeats = ref(props.minSeats)
const coupon = ref('')

const isMinSeats = computed(() => selectedSeats.value === props.minSeats)

const isMigrated = computed(() => props.pricePlan.id.includes('MIGRATED'))

const { formatCurrency } = useCurrency()

const extraSignaturePrices = computed(() => {
  if (!isMigrated.value) return null

  return {
    ses: formatCurrency(
      props.pricePlan.monthly[props.currency.toLowerCase() as Lowercase<Currency>].extraSes,
      props.currency
    ),
    aes: formatCurrency(
      props.pricePlan.monthly[props.currency.toLowerCase() as Lowercase<Currency>].extraAes,
      props.currency
    ),
    qes: formatCurrency(
      props.pricePlan.monthly[props.currency.toLowerCase() as Lowercase<Currency>].extraQes,
      props.currency
    ),
  }
})

const formatPackageName = (name: string) => {
  const screamingSnake = name.replace(/[A-Z]/g, char => `_${char}`).toUpperCase()
  const orString = t('global.or') as string
  return screamingSnake.replace('_', ` ${orString} `)
}

const signaturePackages = computed(() => {
  return Object.entries(props.pricePlan[selectedBillingCycle.value].signaturesIncluded)
    .filter(([_, count]) => count !== 0)
    .map(([name, count]) => ({
      id: name,
      name: formatPackageName(name),
      // -1 means unlimited
      count: count === -1 ? -1 : selectedSeats.value * count,
    }))
})

const seatPrice = computed<number>(() => {
  // FIXME: The backend uses inconsistent casing for currency values
  const lowercaseCurrency = props.currency.toLowerCase() as Lowercase<Currency>
  return props.pricePlan[selectedBillingCycle.value][lowercaseCurrency].seat
})

const discountText = computed(() => {
  const lowercaseCurrency = props.currency.toLowerCase() as Lowercase<Currency>
  const monthlyPrice = props.pricePlan.monthly[lowercaseCurrency].seat
  const yearlyPrice = props.pricePlan.yearly[lowercaseCurrency].seat / 12 // Per month, so we can compare

  if (yearlyPrice === 0) return ''

  const percentage = Math.floor(((monthlyPrice - yearlyPrice) / yearlyPrice) * 100)

  return t('business.upgrade.plan.discount', { percentage })
})

const colorClass = computed(() => `plan-${props.pricePlan.name.toLowerCase()}`)

const isLegalAccepted = ref(false)

const isUpgradePossible = computed(() => !props.isDisabled && isLegalAccepted.value && !props.isLoading)

const isIndividualPlan = computed(() => props.pricePlan.id.startsWith('INDIVIDUAL'))

const smallPrintText = computed<string>(() => {
  if (isMigrated.value) return t('business.upgrade.plan.migrated.small_print')
  if (isLocked.value || isTrialing.value || isStarterUpgrading.value)
    return t('business.upgrade.plan.trial_locked.small_print')
  return t('business.upgrade.plan.small_print')
})

const displayPrice = computed(() => {
  if (!props.preview) return 0
  return props.preview.amountSubtotal - props.preview.amountDiscountTotal
})

const submit = () => {
  if (props.isLoading) return

  const seats = selectedSeats.value !== props.minSeats ? selectedSeats.value : undefined
  emit('upgrade', seats, coupon.value)
}

watch(
  () => [selectedSeats.value, selectedBillingCycle.value, coupon.value],
  async () => {
    emit('update', selectedSeats.value, selectedBillingCycle.value, coupon.value)
  }
)
</script>

<template>
  <div>
    <div class="upgrade-card" data-cy="upgrade_card">
      <div :class="['upgrade-card__hue mb-4', `bg-${colorClass}`]" />
      <section class="pa-8">
        <div class="text-h5 font-weight-bold mb-6">
          <i18n-t keypath="business.upgrade.plan.upgrade_to">
            <template #name>
              <span :class="`text-${colorClass}`">{{ pricePlan.name }}</span>
            </template>
          </i18n-t>
        </div>
        <p v-if="isYearlySubscription">{{ $t('business.upgrade.plan.yearly_lead') }}</p>
        <div v-else class="upgrade-card__cycle">
          <v-radio-group v-model="selectedBillingCycle" inline hide-details>
            <v-radio
              :label="$t('billing.billing_cycle.monthly')"
              value="monthly"
              true-icon="custom:radio_button_active"
              false-icon="custom:radio_button_inactive"
              data-cy="monthly_cycle_radio"
            />
            <v-radio
              :label="$t('billing.billing_cycle.yearly')"
              value="yearly"
              true-icon="custom:radio_button_active"
              false-icon="custom:radio_button_inactive"
              data-cy="yearly_cycle_radio"
            />
          </v-radio-group>
          <div class="text-green font-weight-bold">{{ discountText }}</div>
        </div>
      </section>
      <v-divider />
      <template v-if="!isMigrated">
        <section class="d-flex align-start px-8 py-4">
          <v-icon color="grey-darker">{{ icons.sofa }}</v-icon>
          <div class="flex-grow-1 ml-4">
            <div class="d-flex align-start justify-space-between">
              <div>
                <div>
                  <span class="font-weight-bold">{{ $t('business.upgrade.plan.seats') }}</span>
                  <br />
                  <span data-cy="seat_price_text" :class="{ 'text-green': billingCycle === 'yearly' }">
                    {{
                      $t('business.upgrade.plan.at_price', {
                        // Yearly prices need to be divided by 12 for comparison with monthly prices
                        price: formatCurrency(billingCycle === 'monthly' ? seatPrice : seatPrice / 12, currency),
                      })
                    }}
                  </span>
                  {{ ' ' }}
                  <span>{{ $t('billing.billing_cycle.per_month') }}</span>
                </div>
              </div>
              <span v-if="isIndividualPlan" class="font-weight-bold">{{ minSeats }}</span>
              <number-increment
                v-else
                :incr-enabled="true"
                :decr-enabled="!isMinSeats"
                :show-decr-tooltip="isMinSeats && minSeats > 1"
                :min="minSeats"
                @input="newSeats => (selectedSeats = newSeats)"
              >
                <template v-if="isMinSeats" #decr>{{ $t('business.upgrade.plan.min_seats_info') }}</template>
              </number-increment>
            </div>
            <div v-if="isIndividualPlan" class="text-body-2 text-danger mt-4">
              {{
                isLocked
                  ? $t('business.upgrade.plan.members.blocked_individual_hint')
                  : $t('business.upgrade.plan.members.individual_hint')
              }}
            </div>
            <div v-else-if="isMinSeats && minSeats > 1" class="text-body-2 mt-4">
              {{ $t('business.upgrade.plan.members.reduce_seats') }}
              {{ ' ' }}
              <nuxt-link class="text-no-decoration" :to="{ name: 'business-members' }">
                {{ $t('business.upgrade.plan.members.remove_members') }}
              </nuxt-link>
            </div>
          </div>
        </section>
        <v-divider />
      </template>
      <section class="d-flex align-start px-8 py-4">
        <v-icon color="grey-darker">custom:signature</v-icon>
        <div class="flex-grow-1 ml-4 space-y-4">
          <div v-if="isMigrated">
            <i18n-t keypath="business.upgrade.plan.migrated.signature_prices" tag="p">
              <template #boldText>
                <span class="font-weight-bold">{{ $t('business.upgrade.plan.migrated.pay_per_use') }}</span>
                {{ '–' }}
              </template>
              <template #sesPrice>{{ extraSignaturePrices?.ses }}</template>
              <template #aesPrice>{{ extraSignaturePrices?.aes }}</template>
              <template #qesPrice>{{ extraSignaturePrices?.qes }}</template>
            </i18n-t>
            <i18n-t keypath="business.upgrade.plan.migrated.pay_when_signed" tag="p">
              <template #boldText>
                <span class="font-weight-bold">{{ $t('business.upgrade.plan.migrated.no_monthly_fees') }}</span>
                {{ '–' }}
              </template>
            </i18n-t>
          </div>
          <template v-else>
            <div
              v-for="signaturePackage in signaturePackages"
              :key="signaturePackage.id"
              class="d-flex align-center justify-space-between"
            >
              <span>
                <span class="font-weight-bold">{{ signaturePackage.name }}</span>
                <span v-if="signaturePackage.count !== -1">
                  {{ ' ' }}
                  {{
                    selectedBillingCycle === 'yearly'
                      ? $t('billing.billing_cycle.per_year')
                      : $t('billing.billing_cycle.per_month')
                  }}
                </span>
              </span>
              <span class="font-weight-bold">
                {{ signaturePackage.count === -1 ? $t('global.unlimited') : signaturePackage.count }}
              </span>
            </div>
          </template>
        </div>
      </section>
      <v-divider />
      <section class="agreement_checkbox px-8 py-4">
        <skr-checkbox v-model="isLegalAccepted" data-cy="legal_checkbox" class="mt-0">
          <i18n-t keypath="business.upgrade.plan.legal">
            <template #gtc>
              <a :href="t('business.setup.gtc_dpa.gtc_link_url')" target="_blank" rel="noopener" @click.stop>{{
                $t('business.setup.gtc_dpa.gtc_link_text')
              }}</a>
            </template>
            <template #dpa>
              <a :href="t('business.setup.gtc_dpa.dpa_link_url')" target="_blank" rel="noopener,nofollow" @click.stop>{{
                $t('business.setup.gtc_dpa.dpa_link_text')
              }}</a>
            </template>
          </i18n-t>
        </skr-checkbox>
      </section>
      <v-divider />
      <section v-if="showPromoCodeInput" class="px-8 pt-6">
        <coupon-input
          :existing-discount-promo-code="discountPromoCode"
          :billing-cycle="billingCycle"
          :price-plan="pricePlan"
          :price-data="props.preview"
          :is-fetching-price="isFetchingPrice"
          @apply-coupon="promoCode => (coupon = promoCode)"
          @remove-coupon="() => (coupon = '')"
        />
      </section>
      <section class="px-8 pt-7 pb-6">
        <div class="text-center mb-6">
          <i18n-t keypath="business.upgrade.plan.due_today">
            <template #cycle>
              <span>
                {{
                  selectedBillingCycle === 'yearly'
                    ? $t('billing.billing_cycle.per_year')
                    : $t('billing.billing_cycle.per_month')
                }}
              </span>
            </template>
            <template #price>
              <dancing-dots-loader v-if="isFetchingPrice" />
              <span v-else class="font-weight-bold" data-cy="total_price_text">{{
                formatCurrency(displayPrice ?? 0, currency)
              }}</span>
            </template>
          </i18n-t>
        </div>
        <v-btn
          color="primary"
          size="x-large"
          block
          data-cy="upgrade_button"
          :disabled="!isUpgradePossible"
          :loading="isLoading"
          @click="submit"
        >
          {{ upgradeButtonContent }}
        </v-btn>
      </section>
    </div>
    <div class="small-print">{{ smallPrintText }}</div>
  </div>
</template>

<style lang="scss" scoped>
$card-width: 460px;

.upgrade-card {
  color: $c-grey-darker;
  width: $card-width;
  box-shadow: 0px 2px 5px rgba(40, 62, 102, 0.19);
  border-radius: 4px;
  overflow: hidden;
  background-color: $c-white;

  .v-radio-group {
    flex: 0 1 auto;

    .v-radio {
      margin-right: 16px;

      :deep(.v-selection-control__wrapper) {
        margin-right: 8px;
      }
    }
  }

  &__hue {
    height: 12px;
  }

  &__cycle {
    display: flex;
    align-items: center;
    background-color: #eef5fd;
    padding: 4 * $bw;
    border-radius: 8px;

    :deep(.v-label) {
      font-weight: bold;
      color: $c-grey-darker;
    }
  }
}

.small-print {
  max-width: $card-width;
  font-size: 0.875rem;
  padding: 4 * $bw;
  text-align: center;
  color: $c-grey-dark;
}

.agreement_checkbox {
  :deep(.v-selection-control) {
    align-items: center;

    .v-label {
      margin-left: 14px;
      font-size: 0.875rem;
      color: $c-grey-dark;
    }
  }
}
</style>
